// SPDX-License-Identifier: GPL-3.0-only
// Copyright (C) 2026 Kyle Johnson

import type { ImportResult, ImportedTransaction, ImportedAccount, ImportError, ImportWarning } from './types.js';
import { parseDate } from './csv-parser.js';

// ---------------------------------------------------------------------------
// QIF date parsing
// QIF uses MM/DD'YY or MM/DD/YYYY or DD/MM/YYYY
// ---------------------------------------------------------------------------

function parseQifDate(raw: string): string | null {
  const s = raw.trim();
  // MM/DD'YY or MM/DD'YYYY — apostrophe before 2-digit year
  const apostrophe = s.match(/^(\d{1,2})\/(\d{1,2})'(\d{2,4})$/);
  if (apostrophe) {
    let year = parseInt(apostrophe[3]!);
    if (year < 100) year = year >= 70 ? 1900 + year : 2000 + year;
    return `${year}-${apostrophe[1]!.padStart(2, '0')}-${apostrophe[2]!.padStart(2, '0')}`;
  }
  // Standard date parsing (MDY default for QIF)
  return parseDate(s, true);
}

// ---------------------------------------------------------------------------
// QIF type mapping
// ---------------------------------------------------------------------------

const QIF_TYPE_TO_ACCOUNT_TYPE: Record<string, string> = {
  Bank: 'BANK',
  CCard: 'CREDIT_CARD',
  Cash: 'CASH',
  Invst: 'STOCK',
  'Oth A': 'ASSET',
  'Oth L': 'LIABILITY',
};

export interface QifAccount {
  name: string;
  type: string;
  description: string | undefined;
}

export interface QifRawTransaction {
  date?: string;
  amount?: number;
  payee?: string;
  memo?: string;
  num?: string;
  cleared?: string;
  category?: string;
  transferAccount?: string;
  splits: Array<{ category: string; memo?: string; amount?: number }>;
}

export interface QifParseResult {
  accountSections: Array<{
    account: QifAccount | null;
    accountType: string;
    transactions: QifRawTransaction[];
  }>;
  errors: ImportError[];
  warnings: ImportWarning[];
}

// ---------------------------------------------------------------------------
// Low-level QIF tokenizer
// ---------------------------------------------------------------------------

export function parseQifRaw(content: string): QifParseResult {
  const errors: ImportError[] = [];
  const warnings: ImportWarning[] = [];

  const lines = content.replace(/\r\n/g, '\n').replace(/\r/g, '\n').split('\n');

  const sections: QifParseResult['accountSections'] = [];
  let currentAccountType = 'Bank';
  let currentAccount: QifAccount | null = null;
  let currentTxns: QifRawTransaction[] = [];
  let currentTx: QifRawTransaction = { splits: [] };
  let inAccountBlock = false;
  let accountBuffer: Partial<QifAccount> = {};
  let lineNum = 0;
  let inTransaction = false;

  const finalizeTx = () => {
    if (inTransaction && (currentTx.date !== undefined || currentTx.amount !== undefined || currentTx.payee !== undefined)) {
      currentTxns.push(currentTx);
    }
    currentTx = { splits: [] };
    inTransaction = false;
  };

  const finalizeSection = () => {
    if (inTransaction) finalizeTx();
    sections.push({ account: currentAccount, accountType: currentAccountType, transactions: currentTxns });
    currentTxns = [];
    currentAccount = null;
    inTransaction = false;
    currentTx = { splits: [] };
  };

  for (const raw of lines) {
    lineNum++;
    const line = raw.trimEnd();
    if (!line) continue;

    const code = line[0] ?? '';
    const value = line.slice(1).trim();

    // Account block start
    if (line === '!Account') {
      if (inTransaction) finalizeTx();
      if (currentTxns.length > 0) finalizeSection();
      inAccountBlock = true;
      accountBuffer = {};
      continue;
    }

    if (inAccountBlock) {
      switch (code) {
        case 'N': accountBuffer.name = value; break;
        case 'T': accountBuffer.type = QIF_TYPE_TO_ACCOUNT_TYPE[value] ?? 'ASSET'; break;
        case 'D': accountBuffer.description = value; break;
        case '^':
          currentAccount = { name: accountBuffer.name ?? 'Unknown', type: accountBuffer.type ?? 'BANK', description: accountBuffer.description ?? undefined };
          inAccountBlock = false;
          break;
      }
      continue;
    }

    if (line.startsWith('!Type:')) {
      const type = line.slice(6).trim();
      if (inTransaction) finalizeTx();
      if (currentTxns.length > 0) finalizeSection();
      currentAccountType = QIF_TYPE_TO_ACCOUNT_TYPE[type] ?? 'BANK';
      continue;
    }

    if (line.startsWith('!')) {
      // Unknown directive — skip
      warnings.push({ line: lineNum, message: `Unknown QIF directive: "${line}"` });
      continue;
    }

    switch (code) {
      case 'D':
        inTransaction = true;
        currentTx.date = value;
        break;
      case 'T':
      case 'U': {
        inTransaction = true;
        const amt = parseFloat(value.replace(/,/g, ''));
        if (!isNaN(amt)) currentTx.amount = Math.round(amt * 100);
        break;
      }
      case 'P':
        inTransaction = true;
        currentTx.payee = value;
        break;
      case 'M':
        currentTx.memo = value;
        break;
      case 'N':
        currentTx.num = value;
        break;
      case 'C':
        currentTx.cleared = value;
        break;
      case 'L': {
        // Category / transfer account
        if (value.startsWith('[') && value.endsWith(']')) {
          currentTx.transferAccount = value.slice(1, -1);
        } else {
          currentTx.category = value;
        }
        break;
      }
      case 'S': {
        // Split category
        currentTx.splits.push({ category: value });
        break;
      }
      case 'E': {
        // Split memo — applies to most recent split
        const lastSplitForMemo = currentTx.splits[currentTx.splits.length - 1];
        if (lastSplitForMemo !== undefined) {
          lastSplitForMemo.memo = value;
        }
        break;
      }
      case '$': {
        // Split amount — applies to most recent split
        const splitAmt = parseFloat(value.replace(/,/g, ''));
        const lastSplit = currentTx.splits[currentTx.splits.length - 1];
        if (!isNaN(splitAmt) && lastSplit !== undefined) {
          lastSplit.amount = Math.round(splitAmt * 100);
        }
        break;
      }
      case '^':
        finalizeTx();
        break;
      default:
        if (code && code !== ' ') {
          warnings.push({ line: lineNum, message: `Unknown QIF field code: "${code}"` });
        }
    }
  }

  // Flush final section
  if (inTransaction) finalizeTx();
  if (currentTxns.length > 0 || currentAccount !== null) {
    sections.push({ account: currentAccount, accountType: currentAccountType, transactions: currentTxns });
  }

  return { accountSections: sections, errors, warnings };
}

// ---------------------------------------------------------------------------
// Public parse API
// ---------------------------------------------------------------------------

export interface QifParseOptions {
  /** Map from QIF account name → Hushold account ID */
  accountMapping: Record<string, string>;
  /** Fallback account ID if QIF has no !Account block */
  defaultAccountId: string;
}

export function parseQif(content: string, options: QifParseOptions): ImportResult {
  const raw = parseQifRaw(content);
  const errors = [...raw.errors];
  const warnings = [...raw.warnings];
  const transactions: ImportedTransaction[] = [];
  const accounts: ImportedAccount[] = [];
  const accountsSeen = new Set<string>();

  for (const section of raw.accountSections) {
    const accountId = section.account
      ? (options.accountMapping[section.account.name] ?? options.defaultAccountId)
      : options.defaultAccountId;

    if (section.account && !accountsSeen.has(section.account.name)) {
      accountsSeen.add(section.account.name);
      accounts.push({
        externalId: section.account.name,
        name: section.account.name,
        type: section.account.type,
      });
    }

    for (const rawTx of section.transactions) {
      if (rawTx.date === undefined) {
        errors.push({ message: 'Transaction missing date', rawData: JSON.stringify(rawTx) });
        continue;
      }
      if (rawTx.amount === undefined) {
        errors.push({ message: 'Transaction missing amount', rawData: JSON.stringify(rawTx) });
        continue;
      }

      const date = parseQifDate(rawTx.date);
      if (!date) {
        errors.push({ message: `Cannot parse date: "${rawTx.date}"`, rawData: JSON.stringify(rawTx) });
        continue;
      }

      let transferAccountId: string | undefined;
      if (rawTx.transferAccount) {
        transferAccountId = options.accountMapping[rawTx.transferAccount];
        if (!transferAccountId) {
          warnings.push({ message: `Transfer account "${rawTx.transferAccount}" not mapped; ignored` });
        }
      }

      transactions.push({
        date,
        description: rawTx.payee ?? '',
        amount: rawTx.amount,
        currency: 'USD',
        accountId,
        ...(rawTx.memo ? { memo: rawTx.memo } : {}),
        ...(rawTx.num ? { num: rawTx.num } : {}),
        ...(transferAccountId ? { transferAccountId } : {}),
        rawData: { date: rawTx.date, payee: rawTx.payee ?? '', amount: String(rawTx.amount) },
      });
    }
  }

  const dates = transactions.map((t) => t.date).sort();
  return {
    transactions, accounts, errors, warnings,
    format: 'qif',
    dateRange: { start: dates[0] ?? '', end: dates[dates.length - 1] ?? '' },
    totalTransactions: transactions.length,
  };
}
